import { Box, useTheme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Flex from "../Flex";
import { OptionRow } from "./OptionRow";
import { MData } from "./types";

interface SelectedOptionsSectionProps<T extends MData> {
  selectedItems: T[];
  labelKey: keyof T;
  showAllSelected: boolean;
  onToggleShowAll: () => void;
  onToggleOption: (opt: T) => void;
  onClearAll: () => void;
  renderCustomLabel?: (opt: T) => React.ReactNode;
}

export const SelectedOptionsSection = <T extends MData>({
  selectedItems,
  labelKey,
  showAllSelected,
  onToggleShowAll,
  onToggleOption,
  onClearAll,
  renderCustomLabel,
}: SelectedOptionsSectionProps<T>) => {
  const theme = useTheme();
  if (selectedItems.length === 0) return null;

  const visible = showAllSelected ? selectedItems : selectedItems.slice(0, 1);

  return (
    <Box>
      <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
        {visible.map((opt) => (
          <OptionRow
            key={String(opt[labelKey])}
            option={opt}
            labelKey={labelKey}
            selected
            onToggle={onToggleOption}
            renderCustomLabel={renderCustomLabel}
          />
        ))}
      </Box>

      <Flex
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.25,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Flex
          alignItems="center"
          gap={0.5}
          onClick={onToggleShowAll}
          sx={{
            cursor: "pointer",
            color: theme.palette.primary.main,
            fontSize: "13px",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <span>
            {showAllSelected
              ? "Hide All Selected Options"
              : "Show All Selected Options"}
          </span>
          {showAllSelected ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )}
        </Flex>

        <Box
          component="button"
          type="button"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClearAll();
          }}
          sx={{
            background: "none",
            border: "none",
            p: 0,
            cursor: "pointer",
            color: theme.palette.error.main,
            fontSize: "13px",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Clear All
        </Box>
      </Flex>
    </Box>
  );
};
